import * as React from 'react'
import { useFormikContext } from 'formik'

import Alert from '@mui/material/Alert'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'

import { ENTITY_TYPES } from '@sales-center/common'

import { OwnerSelect } from '@user/components/Owner'
import { LeadAssignmentRuleInfoPopoverButton } from '@user/components/Settings/LeadAssignmentRule/LeadAssignmentRuleInfo/LeadAssignmentRuleInfoPopoverButton'
import { LeadStatusSelect } from '@user/components/Leads/AddLeadPage/FormFields/LeadStatusSelect/LeadStatusSelect'
import { SalesFunnelSelect } from '@user/components/Leads/AddLeadPage/FormFields/SalesFunnelSelect/SalesFunnelSelect'
import { SalesTeamSelect } from '@user/components/Leads/AddLeadPage/FormFields/SalesTeamSelect/SalesTeamSelect'
import type { IAutoReviewMissingField } from '@user/store/draftLeads/typings'

import type { UpdateRequiredFieldsFormModel } from '../typings'
import { useAssignmentFields } from './hooks/useAssignmentFields'

interface Props {
  /** The assignment group's missing fields; only these inputs are rendered. */
  fields: IAutoReviewMissingField[]
}

/**
 * Sales team, owner, sales funnel and lead status inputs for the assignment group, with Add Lead's
 * cascades (team resets owner, funnel clears status) and automated assignment rule handling. A
 * matching rule fills team, owner, funnel and status, even for values that are not rendered.
 */
export function AssignmentFields(props: Props) {
  const { fields } = props
  const { values } = useFormikContext<UpdateRequiredFieldsFormModel>()
  const data = useAssignmentFields()
  const { rule } = data

  const has = (key: string) => fields.some((field) => field.fieldKey === key)

  return (
    <Stack spacing={2}>
      {!!rule && !data.isOverriden && (
        <Alert severity="info">
          <Typography variant="body2">
            One or more values entered has triggered an automated lead
            assignment rule which has preselected the ownership of this lead.{' '}
            <LeadAssignmentRuleInfoPopoverButton
              label="View details"
              rule={rule}
            />
          </Typography>
        </Alert>
      )}
      {has('salesTeamId') && (
        <SalesTeamSelect
          disabled={data.salesTeamDisabled}
          name="salesTeamId"
          label="Sales Team"
          optionList={data.salesTeams}
          onChange={data.onChangeSalesTeam}
          errorText={data.selectedSalesTeamAccessText}
        />
      )}
      {has('assigneeId') && (
        <OwnerSelect
          salesTeamId={String(values.salesTeamId ?? '')}
          entityType={ENTITY_TYPES.Lead}
          name="assigneeId"
          label="Owner"
          required
          errorText={data.positionBasedAccessText}
          disabled={data.ownerDisabled}
          onChange={data.onChangeOwner}
          testId="auto-review-owner-select"
          defaultSetRequired={false}
          defaultValue={data.defaultOwnerId}
          ignoreDefault={data.ignoreDefault}
          assignmentRuleData={{
            rule,
            canOverride: data.canOverrideAutomatedAssignment,
          }}
        />
      )}
      {has('salesFunnelId') && (
        <SalesFunnelSelect
          disabled={data.lockedByRule}
          name="salesFunnelId"
          label="Sales Funnel"
          salesFunnels={data.salesFunnels}
          onChange={data.onChangeSalesFunnel}
        />
      )}
      {has('salesFunnelStageId') && (
        <LeadStatusSelect
          disabled={data.lockedByRule}
          name="salesFunnelStageId"
          label="Lead Status"
          onChange={data.onChangeStatus}
          salesFunnelStages={data.salesFunnelStages}
        />
      )}
    </Stack>
  )
}
AssignmentFields.displayName = 'AssignmentFields'
